import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api, API_BASE } from '../lib/api';
import { getRawToken } from '../lib/tokenStorage';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, RefreshCw, Download, FileText, ExternalLink, Filter, X, User, Calendar as CalIcon, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

export default function TranscriptsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [filtersMeta, setFiltersMeta] = useState({ stages: [], agents: [] });

  const [agentId, setAgentId] = useState('all');
  const [stage, setStage] = useState('all');
  const [clientQuery, setClientQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [textQ, setTextQ] = useState('');
  const [debouncedTextQ, setDebouncedTextQ] = useState('');
  const [debouncedClientQ, setDebouncedClientQ] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTextQ(textQ), 400);
    return () => clearTimeout(t);
  }, [textQ]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedClientQ(clientQuery), 400);
    return () => clearTimeout(t);
  }, [clientQuery]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/transcripts/_meta/filters').then(({ data }) => setFiltersMeta(data)).catch(() => {});
  }, [isAdmin]);

  const buildParams = useCallback(() => {
    const p = { limit, offset };
    if (agentId !== 'all') p.agent_id = agentId;
    if (stage !== 'all') p.stage = stage;
    if (debouncedClientQ.trim()) p.client_query = debouncedClientQ.trim();
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (debouncedTextQ.trim()) p.q = debouncedTextQ.trim();
    return p;
  }, [agentId, stage, debouncedClientQ, dateFrom, dateTo, debouncedTextQ, limit, offset]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get('/transcripts', { params: buildParams() });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento trascrizioni');
    } finally { setLoading(false); }
  }, [buildParams, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setAgentId('all'); setStage('all'); setClientQuery(''); setDateFrom(''); setDateTo(''); setTextQ(''); setOffset(0);
  };

  const exportUrl = (format) => {
    // ★ 2026-07-29 — use tokenStorage instead of raw localStorage.
    const token = getRawToken();
    const params = new URLSearchParams();
    Object.entries(buildParams()).forEach(([k, v]) => {
      if (k === 'limit' || k === 'offset') return;
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    const url = `${API_BASE}/transcripts/export.${format}?${params.toString()}`;
    // Fetch with auth then download blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `trascrizioni.${format}`;
        a.click();
      })
      .catch((err) => toast.error(`Errore export ${format}: ${err.message}`));
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground" />
            <h2 className="text-lg font-serif mt-2">Sezione riservata</h2>
            <p className="text-sm text-muted-foreground">L&apos;archivio trascrizioni è accessibile solo agli amministratori.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="transcripts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">Archivio Trascrizioni</h1>
          <p className="text-sm text-muted-foreground">Tutte le trascrizioni delle call, divise per agente, cliente, stage. Solo per amministratori.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="transcripts-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" />Aggiorna</Button>
          <Button variant="outline" size="sm" onClick={() => exportUrl('csv')} data-testid="transcripts-export-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
          <Button size="sm" onClick={() => exportUrl('xlsx')} data-testid="transcripts-export-xlsx"><Download className="w-4 h-4 mr-1" />Excel</Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Cerca nel testo della trascrizione…"
                value={textQ}
                onChange={(e) => { setTextQ(e.target.value); setOffset(0); }}
                className="pl-8"
                data-testid="transcripts-text-search"
              />
            </div>
          </div>
          <Input
            placeholder="Cliente (nome/email)"
            value={clientQuery}
            onChange={(e) => { setClientQuery(e.target.value); setOffset(0); }}
            data-testid="transcripts-client-search"
          />
          <Select value={agentId} onValueChange={(v) => { setAgentId(v); setOffset(0); }}>
            <SelectTrigger data-testid="transcripts-agent-filter"><SelectValue placeholder="Agente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli agenti</SelectItem>
              {(filtersMeta.agents || []).map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}{a.role_label ? ` · ${a.role_label}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stage} onValueChange={(v) => { setStage(v); setOffset(0); }}>
            <SelectTrigger data-testid="transcripts-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stage</SelectItem>
              {(filtersMeta.stages || []).map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-1">
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }} data-testid="transcripts-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setOffset(0); }} data-testid="transcripts-date-to" />
          </div>
        </CardContent>
        <CardContent className="pt-0 pb-3 px-3 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Totale risultati: <strong data-testid="transcripts-total">{total}</strong>
          </div>
          <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="transcripts-reset-filters">
            <X className="w-4 h-4 mr-1" />Reset filtri
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-16 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto" />
              <p className="text-sm mt-2">Nessuna trascrizione trovata con questi filtri.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transcripts-table">
                <thead className="bg-[var(--surface-2)] text-left">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cliente</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Agente</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Stage</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Data</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contenuti</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((r) => (
                    <tr key={r.lead_id} className="hover:bg-[var(--surface-2)]/50" data-testid={`transcripts-row-${r.lead_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.client_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{r.client_email || ''}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm">{(r.owner || {}).name || <span className="italic text-muted-foreground">Nessun agente</span>}</div>
                        <div className="text-xs text-muted-foreground">{(r.owner || {}).role_label || ''}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs px-2 py-0.5 rounded border" style={{ borderColor: r.stage_color, color: r.stage_color, backgroundColor: r.stage_color + '15' }}>
                          {r.stage_label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{fmtDate(r.inserted_at)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {r.has_transcript && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Trascriz.</span>}
                          {r.has_proposal && <span className="text-[10px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">Proposta</span>}
                          {r.has_report && <span className="text-[10px] bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded">Report</span>}
                        </div>
                        {r.transcript_preview && (
                          <div className="text-[11px] text-muted-foreground line-clamp-2 mt-1 max-w-md">{r.transcript_preview}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setPreview(r)} data-testid={`transcripts-view-${r.lead_id}`}><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={`/app/leads/${r.lead_id}`} data-testid={`transcripts-lead-${r.lead_id}`}><ExternalLink className="w-4 h-4" /></Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {items.length > 0 && total > limit && (
          <div className="p-3 flex items-center justify-between border-t">
            <div className="text-xs text-muted-foreground">Pagina {Math.floor(offset / limit) + 1} di {Math.ceil(total / limit)}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Precedente</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Successiva</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Preview dialog (full transcript) */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="transcripts-preview-dialog">
          <DialogHeader>
            <DialogTitle>{preview?.client_name}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2 text-xs mt-1">
              <span>{preview?.client_email}</span>
              <span>·</span>
              <span>{preview?.stage_label}</span>
              <span>·</span>
              <span>Agente: {(preview?.owner || {}).name || '—'}</span>
              <span>·</span>
              <span>Data: {fmtDate(preview?.inserted_at)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {preview?.transcript_full && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Trascrizione</div>
                <div className="whitespace-pre-wrap text-sm p-3 bg-[var(--surface-2)] rounded-md border max-h-[45vh] overflow-y-auto">{preview.transcript_full}</div>
              </div>
            )}
            {preview?.proposta_url && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Proposta</div>
                <a href={preview.proposta_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[hsl(var(--primary))] hover:underline break-all"><ExternalLink className="w-3 h-3 inline mr-1" />{preview.proposta_url}</a>
              </div>
            )}
            {preview?.report_full && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Report</div>
                <div className="whitespace-pre-wrap text-sm p-3 bg-[var(--surface-2)] rounded-md border">{preview.report_full}</div>
              </div>
            )}
            <div className="flex justify-end">
              <Button asChild variant="outline" size="sm">
                <Link to={`/app/leads/${preview?.lead_id}`}><ExternalLink className="w-4 h-4 mr-1" />Apri scheda cliente</Link>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
